import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db, functions } from '../firebase';
import type { CustomerFeatures } from '../lib/db/types';
import type { TenantAccessSummary, TenantMembership, UserRole } from '../lib/db/types';

interface TenantContextType {
  user: User | null;
  tenantId: string | null;
  role: UserRole | null;
  roles: UserRole[];
  tenants: TenantAccessSummary[];
  activeMembership: TenantMembership | null;
  customerFeatures: CustomerFeatures | null;
  loading: boolean;
  switchTenant: (tenantId: string) => void;
  refreshTenants: () => Promise<void>;
  refreshClaims: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType>({
  user: null,
  tenantId: null,
  role: null,
  roles: [],
  tenants: [],
  activeMembership: null,
  customerFeatures: null,
  loading: true,
  switchTenant: () => {},
  refreshTenants: async () => {},
  refreshClaims: async () => {},
});

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

const activeTenantStorageKey = (uid: string) => `crm.activeTenantId.${uid}`;

export const TenantProvider = ({ children }: TenantProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenants, setTenants] = useState<TenantAccessSummary[]>([]);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  const [customerFeatures, setCustomerFeatures] = useState<CustomerFeatures | null>(null);
  const [loading, setLoading] = useState(true);

  const activeTenant = useMemo(
    () => tenants.find((tenant) => tenant.tenantId === activeTenantId) || null,
    [activeTenantId, tenants]
  );

  const refreshTenants = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setTenants([]);
      setActiveTenantId(null);
      return;
    }

    const listMyTenants = httpsCallable(functions, 'listMyTenants');
    const result = await listMyTenants();
    const data = result.data as { tenants?: TenantAccessSummary[] };
    const nextTenants = data.tenants || [];
    setTenants(nextTenants);

    const storedTenantId = localStorage.getItem(activeTenantStorageKey(currentUser.uid));
    const nextActiveTenant =
      nextTenants.find((tenant) => tenant.tenantId === storedTenantId) ||
      nextTenants[0] ||
      null;

    setActiveTenantId(nextActiveTenant?.tenantId || null);
    if (nextActiveTenant) {
      localStorage.setItem(activeTenantStorageKey(currentUser.uid), nextActiveTenant.tenantId);
    }
  };

  const switchTenant = (tenantId: string) => {
    const found = tenants.find((tenant) => tenant.tenantId === tenantId);
    if (!found || !user) return;
    localStorage.setItem(activeTenantStorageKey(user.uid), tenantId);
    setActiveTenantId(tenantId);
  };

  // Backward-compatible name for pages that used to refresh custom claims.
  const refreshClaims = refreshTenants;

  useEffect(() => {
    const tenantId = activeTenantId;
    if (!tenantId) {
      setCustomerFeatures(null);
      return;
    }

    const customerRef = doc(db, 'customers', tenantId);
    const unsubscribe = onSnapshot(
      customerRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const features: CustomerFeatures | null = data?.features ?? null;
          setCustomerFeatures(features);
        } else {
          setCustomerFeatures(null);
        }
      },
      (error) => {
        console.error('Error fetching customer features:', error);
        setCustomerFeatures(null);
      }
    );

    return () => unsubscribe();
  }, [activeTenantId]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setLoading(true);

      try {
        if (currentUser) {
          await refreshTenants();
        } else {
          setTenants([]);
          setActiveTenantId(null);
          setCustomerFeatures(null);
        }
      } catch (error) {
        console.error('Error loading tenants:', error);
        setTenants([]);
        setActiveTenantId(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const value: TenantContextType = {
    user,
    tenantId: activeTenant?.tenantId || null,
    role: activeTenant?.role || null,
    roles: activeTenant?.roles || [],
    tenants,
    activeMembership: activeTenant?.membership || null,
    customerFeatures,
    loading,
    switchTenant,
    refreshTenants,
    refreshClaims,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};
